import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircleIcon, StarIcon, ClockIcon, UsersIcon } from 'lucide-react';
import { ServiceNowSubNav } from '../components/ServiceNowSubNav';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.5,
      ease: 'easeOut'
    }
  })
};
export function ServicesPage() {
  return <div className="w-full" data-id="element-3039" data-mp="2g9oa7ik23ado">
      <ServiceNowSubNav data-id="element-3040" data-mp="2g8mpemd8n9ir" />
      {/* HERO */}
      <section className="section-navy hero-grid-pattern" data-id="element-3041" data-mp="2g7lcsdgkqeyx">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15 text-center" data-id="element-3042" data-mp="2l1fvbdkg0nwf">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-3043" data-mp="2pzr3ljv3aqcj">
            <div className="inline-block px-3 py-1 bg-nrs-orange/20 border border-nrs-orange/40 rounded-full text-nrs-orange text-xs font-semibold uppercase tracking-wider mb-4" data-id="element-3044" data-mp="2zx5l7msr1l6f">
              Complete ServiceNow Services
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight max-w-4xl mx-auto" data-id="element-3045" data-mp="2sjd8omfe525h">
              Governance + Development.
              <br data-id="element-3046" data-mp="2jk9off4hmgpe" />
              <span className="text-nrs-orange" data-id="element-3047" data-mp="2jkrpcp23hj3n">
                Everything Under One Roof.
              </span>
            </h1>
            <div className="h-1.5 w-24 bg-nrs-orange mx-auto rounded-full mb-6" data-id="element-3048" data-mp="3g97kd0nz4ko5"></div>
            <p className="text-xl text-nrs-light font-bold mb-4 max-w-2xl mx-auto" data-id="element-3049" data-mp="3bch4adc65p2q">
              Most clients engage us for both governance and development. Choose
              the services that fit your needs—or let us design a complete
              platform stewardship program.
            </p>
          </motion.div>
        </div>
      </section>

      {/* GOVERNANCE SERVICES */}
      <section className="section-white" data-id="element-3050" data-mp="3ua1srjb0ulmt">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-3051" data-mp="3u90ihh1ty365">
          <div className="flex items-center gap-4 mb-10" data-id="element-3052" data-mp="1sdt09myxfalq">
            <div className="h-10 w-1.5 bg-nrs-orange rounded-full" data-id="element-3053" data-mp="1xbuosnf4g24l" />
            <div data-id="element-3054" data-mp="1xb2y9r0dsul6">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-nrs-navy" data-id="element-3055" data-mp="1xdp51ofg762c">
                Governance Services
              </h2>
              <p className="text-gray-500 text-sm mt-1" data-id="element-3056" data-mp="1nkzssrc2po5y">
                Strategic oversight, policy frameworks, and platform governance
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-3057" data-mp="1elmsop9uabxw">
            {/* Card 1 */}
            <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="border border-gray-200 rounded-xl overflow-hidden" data-id="element-3058" data-mp="zolxucwcq0v7">
              <div className="bg-nrs-light px-6 py-5 border-b border-gray-200" data-id="element-3059" data-mp="2u1vcej3r8ckb">
                <div className="text-nrs-orange font-heading font-bold text-xs uppercase tracking-wider mb-2" data-id="element-3060" data-mp="2z0y5r6adgucg">
                  3-Month Engagement
                </div>
                <h3 className="font-heading font-bold text-lg text-nrs-navy" data-id="element-3061" data-mp="nbs25ovm6wms">
                  Governance Foundations + Application Rationalization
                </h3>
              </div>
              <div className="px-6 py-5" data-id="element-3062" data-mp="x4z5732vu0jp">
                <p className="text-gray-600 text-sm mb-4" data-id="element-3063" data-mp="sd7jb5uo8izw">
                  For agencies with a new platform OR established platform with
                  application sprawl and no governance framework.
                </p>
                <div className="space-y-4" data-id="element-3064" data-mp="121pk3n2yaq3j">
                  <div data-id="element-3065" data-mp="1253larzvot9e">
                    <div className="font-heading font-semibold text-xs text-nrs-blue uppercase tracking-wider mb-2" data-id="element-3066" data-mp="1g2a1cet7wdux">
                      Technical Policy Governance
                    </div>
                    <ul className="space-y-1" data-id="element-3067" data-mp="3htn5h1nd2hpx">
                      <li className="flex items-start gap-2 text-xs text-gray-600" data-id="element-3068" data-mp="3huf1ueos8jh9">
                        <CheckCircleIcon className="h-3.5 w-3.5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-3069" data-mp="3cvtzo9pyzpq6" />
                        Architecture standards and review process
                      </li>
                      <li className="flex items-start gap-2 text-xs text-gray-600" data-id="element-3070" data-mp="3ja0z1yn8fww1">
                        <CheckCircleIcon className="h-3.5 w-3.5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-3071" data-mp="3o9luhz371n40" />
                        Coding standards and best practices enforcement
                      </li>
                      <li className="flex items-start gap-2 text-xs text-gray-600" data-id="element-3072" data-mp="2vm9a9vki9rdv">
                        <CheckCircleIcon className="h-3.5 w-3.5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-3073" data-mp="30kb2p73tn320" />
                        Technical debt inventory with upgrade impact scoring
                      </li>
                      <li className="flex items-start gap-2 text-xs text-gray-600" data-id="element-3074" data-mp="3aaw0wtfl25ke">
                        <CheckCircleIcon className="h-3.5 w-3.5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-3075" data-mp="2cv03hu24dgpz" />
                        Upgrade roadmap and safety validation
                      </li>
                    </ul>
                  </div>
                  <div data-id="element-3076" data-mp="yxx94xbti4fd">
                    <div className="font-heading font-semibold text-xs text-nrs-blue uppercase tracking-wider mb-2" data-id="element-3077" data-mp="yy5ocvvexltq">
                      Data Governance
                    </div>
                    <ul className="space-y-1" data-id="element-3078" data-mp="pwz3kalqdouo">
                      <li className="flex items-start gap-2 text-xs text-gray-600" data-id="element-3079" data-mp="kz603pbtb8y3">
                        <CheckCircleIcon className="h-3.5 w-3.5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-3080" data-mp="l1jtzuuraf42" />
                        Master data ownership framework
                      </li>
                      <li className="flex items-start gap-2 text-xs text-gray-600" data-id="element-3081" data-mp="b830yt2g141g">
                        <CheckCircleIcon className="h-3.5 w-3.5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-3082" data-mp="78anc1y707p8" />
                        Integration architecture patterns and standards
                      </li>
                      <li className="flex items-start gap-2 text-xs text-gray-600" data-id="element-3083" data-mp="3tehe4psw19ys">
                        <CheckCircleIcon className="h-3.5 w-3.5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-3084" data-mp="3oh7ekhbsbhx8" />
                        Cross-instance sync strategy (Secure On-Premise)
                      </li>
                    </ul>
                  </div>
                  <div data-id="element-3085" data-mp="35rltqh0lyuj7">
                    <div className="font-heading font-semibold text-xs text-nrs-blue uppercase tracking-wider mb-2" data-id="element-3086" data-mp="35s3wlwhs0703">
                      Application Rationalization
                    </div>
                    <ul className="space-y-1" data-id="element-3087" data-mp="1cvsamgi3b2b">
                      <li className="flex items-start gap-2 text-xs text-gray-600" data-id="element-3088" data-mp="1e5tvlmkb1dp">
                        <CheckCircleIcon className="h-3.5 w-3.5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-3089" data-mp="6b5q74tm0prm" />
                        Complete inventory of all agency applications (50-200
                        systems)
                      </li>
                      <li className="flex items-start gap-2 text-xs text-gray-600" data-id="element-3090" data-mp="3spp30x2xrwvz">
                        <CheckCircleIcon className="h-3.5 w-3.5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-3091" data-mp="ka5c44pqmqk3" />
                        ServiceNow fit analysis per application
                      </li>
                      <li className="flex items-start gap-2 text-xs text-gray-600" data-id="element-3092" data-mp="29rki3qr2ycul">
                        <CheckCircleIcon className="h-3.5 w-3.5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-3093" data-mp="24tracta4pas7" />
                        Migration roadmap with prioritization
                      </li>
                      <li className="flex items-start gap-2 text-xs text-gray-600" data-id="element-3094" data-mp="2eleu080g1nj4">
                        <CheckCircleIcon className="h-3.5 w-3.5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-3095" data-mp="1qvkjvy7ied2k" />
                        Business cases for top 10 candidates
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="mt-5 bg-green-50 rounded-lg p-3 border border-green-100" data-id="element-3096" data-mp="1vtm2ki10wadi">
                  <p className="text-xs text-green-800 font-medium" data-id="element-3097" data-mp="1vvyv1p4eq34t">
                    Outcome: You know what to build, how to build it, whether
                    ServiceNow is the right platform, and who should build it.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Card 2 */}
            <motion.div custom={1} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="border border-gray-200 rounded-xl overflow-hidden" data-id="element-3098" data-mp="19kfcsv0twq9t">
              <div className="bg-nrs-light px-6 py-5 border-b border-gray-200" data-id="element-3099" data-mp="1jba1s75gm3yy">
                <div className="text-nrs-orange font-heading font-bold text-xs uppercase tracking-wider mb-2" data-id="element-3100" data-mp="1oamkcfpasovq">
                  Ongoing Retainer
                </div>
                <h3 className="font-heading font-bold text-lg text-nrs-navy" data-id="element-3101" data-mp="1sdrik62ci4ek">
                  Fractional Platform Owner
                </h3>
              </div>
              <div className="px-6 py-5" data-id="element-3102" data-mp="228jk1kz1tbem">
                <p className="text-gray-600 text-sm mb-4" data-id="element-3103" data-mp="227i6n9uk8ctq">
                  For agencies with an established platform needing continuous
                  governance oversight.
                </p>
                <ul className="space-y-2" data-id="element-3104" data-mp="7xoaafc4nqa9">
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-3105" data-mp="ddpuhmlv53h">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-3106" data-mp="3rqgfnksjsdo2" />
                    10-15 hours/week embedded governance advisor
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-3107" data-mp="3hstz6tvloe1z">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-3108" data-mp="3hqplxlbta2p1" />
                    Technical Policy, Data, Security/ATO, and Strategic
                    Governance
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-3109" data-mp="9d0jgh93f6lz">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-3110" data-mp="9etoxt7yzkse" />
                    Monthly governance board facilitation
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-3111" data-mp="2u4stdspt3bq9">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-3112" data-mp="2p33jwhi8f64a" />
                    Quarterly maturity assessments
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-3113" data-mp="2r6901i5ro3g4">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-3114" data-mp="2r44oce1mm05a" />
                    SI/vendor oversight (works with New River Systems
                    development team OR external SIs)
                  </li>
                </ul>
                <div className="mt-5 bg-blue-50 rounded-lg p-3 border border-blue-100" data-id="element-3115" data-mp="3etywwjewx2jq">
                  <p className="text-xs text-nrs-blue font-medium" data-id="element-3116" data-mp="3at6mvny7utme">
                    Annual contract. Senior practitioner. Significant savings
                    vs. a full-time hire.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Card 3 */}
            <motion.div custom={2} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="border border-gray-200 rounded-xl overflow-hidden" data-id="element-3117" data-mp="1ughv0ozumxw2">
              <div className="bg-nrs-light px-6 py-5 border-b border-gray-200" data-id="element-3118" data-mp="4zw3yr7md7es">
                <div className="text-nrs-orange font-heading font-bold text-xs uppercase tracking-wider mb-2" data-id="element-3119" data-mp="9wmfr2ce5gue">
                  Governance Automation
                </div>
                <h3 className="font-heading font-bold text-lg text-nrs-navy" data-id="element-3120" data-mp="7l3nn8yvjlp">
                  Platform Command Center
                </h3>
              </div>
              <div className="px-6 py-5" data-id="element-3121" data-mp="3ic669tlogl5r">
                <p className="text-gray-600 text-sm mb-4" data-id="element-3122" data-mp="3nd3m3zj7t343">
                  Custom scoped application with automated governance tooling
                  built directly into your ServiceNow instance.
                </p>
                <ul className="space-y-2" data-id="element-3123" data-mp="3dffrd9llzvlx">
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-3124" data-mp="3dgh4djm6qs6a">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-3125" data-mp="16ogdinauitmv" />
                    Automated demand scoring dashboard
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-3126" data-mp="1a6vv3c2sgtdr">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-3127" data-mp="156858tqaqil4" />
                    Governance maturity tracker
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-3128" data-mp="109hrwe5vm13e">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-3129" data-mp="w7w808uxcmjp" />
                    Technical debt ledger with scoring
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-3130" data-mp="1nyah7rrztap1">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-3131" data-mp="1j60qwamyu8m8" />
                    ATO compliance dashboard
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-3132" data-mp="1f34i1bn89qvh">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-3133" data-mp="2bo4q5y96yf40" />
                    Data governance registry
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-3134" data-mp="3pjmxdqfidaqs">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-3135" data-mp="3uhock09tcpwz" />
                    Application rationalization tracker
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* DEVELOPMENT SERVICES */}
      <section className="section-light-blue" data-id="element-3136" data-mp="cd4ozv5l50a2">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-3137" data-mp="ca8r26tqmblm">
          <div className="flex items-center gap-4 mb-10" data-id="element-3138" data-mp="ha4spzeypqy0">
            <div className="h-10 w-1.5 bg-nrs-blue rounded-full" data-id="element-3139" data-mp="m2ej1gjzot0t" />
            <div data-id="element-3140" data-mp="m491mo7sg91q">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-nrs-navy" data-id="element-3141" data-mp="q3q89kyybc8h">
                Development Services
              </h2>
              <p className="text-gray-500 text-sm mt-1" data-id="element-3142" data-mp="v45l8bfbmhpx">
                Expert ServiceNow implementation, operations, and specialized
                development
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-3143" data-mp="mdm6qtraixqi">
            {/* Platform Dev */}
            <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="bg-white rounded-xl overflow-hidden border border-gray-200" data-id="element-3144" data-mp="hfkky12hckbc">
              <div className="bg-nrs-blue px-6 py-5" data-id="element-3145" data-mp="r80595191kze">
                <div className="text-blue-200 font-heading font-bold text-xs uppercase tracking-wider mb-2" data-id="element-3146" data-mp="3fjco0jvyalu">
                  Full Implementation
                </div>
                <h3 className="font-heading font-bold text-xl text-white" data-id="element-3147" data-mp="deimhmge0caf">
                  Platform Development & Integration
                </h3>
                <div className="mt-2 flex items-center gap-4" data-id="element-3148" data-mp="8emj9pfikovx">
                  <span className="text-blue-200 text-sm" data-id="element-3149" data-mp="1ot5wu4wz8gre">3-9 months</span>
                </div>
              </div>
              <div className="px-6 py-5" data-id="element-3150" data-mp="1jvdsucj2546u">
                <p className="text-gray-600 text-sm mb-4" data-id="element-3151" data-mp="1jwoxsfo8ohzj">
                  For agencies needing ServiceNow implementation, module
                  buildout, or major enhancements.
                </p>
                <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-3152" data-mp="1wb3sb5ibprbm">
                  <div data-id="element-3153" data-mp="1iegrgrn92h0f">
                    <div className="font-heading font-semibold text-xs text-nrs-blue uppercase tracking-wider mb-2" data-id="element-3154" data-mp="1if8cj8j80wcg">
                      Core Modules
                    </div>
                    <ul className="space-y-1 text-xs text-gray-600" data-id="element-3155" data-mp="2a5wcih95rvoj">
                      <li data-id="element-3156" data-mp="2f47lyqvjk468">• ITSM (Incident, Problem, Change, CMDB)</li>
                      <li data-id="element-3157" data-mp="2f5iqwu0q3hyx">• HRSD (Onboarding, Case Mgmt, ESC)</li>
                      <li data-id="element-3158" data-mp="21brr4dcsncge">• Security Operations (VR, TI, SIR)</li>
                      <li data-id="element-3159" data-mp="25d39pyx92xf3">• SPM (Demand, Project, Agile)</li>
                      <li data-id="element-3160" data-mp="25bie05z798fn">• CSM (Case Mgmt, Portals)</li>
                      <li data-id="element-3161" data-mp="2t2nt2ixbfwow">• ITOM (Discovery, Service Mapping)</li>
                    </ul>
                  </div>
                  <div data-id="element-3162" data-mp="2xvqnsudtxc6o">
                    <div className="font-heading font-semibold text-xs text-nrs-blue uppercase tracking-wider mb-2" data-id="element-3163" data-mp="2xw0hot1zw3hn">
                      Specialized
                    </div>
                    <ul className="space-y-1 text-xs text-gray-600" data-id="element-3164" data-mp="33p2ppgz2nzkn">
                      <li data-id="element-3165" data-mp="2yoex4qv73aob">• Secure On-Premise Instances</li>
                      <li data-id="element-3166" data-mp="2tqdf899i9hef">• Custom Applications</li>
                      <li data-id="element-3167" data-mp="2tpljmyfwrjo5">• Complex Integrations</li>
                      <li data-id="element-3168" data-mp="2orabcs59hh81">• Data Migration</li>
                    </ul>
                  </div>
                </div>
                <div className="space-y-1.5" data-id="element-3169" data-mp="2k01wvhp2r5o0">
                  <div className="flex items-center gap-2 text-xs text-gray-700" data-id="element-3170" data-mp="2jzadd58r4ye3">
                    <CheckCircleIcon className="h-3.5 w-3.5 text-green-500" data-id="element-3171" data-mp="2fzruef0hqxdw" />
                    Self-Governing Development: built-in architecture review
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-700" data-id="element-3172" data-mp="2612bqk0rt8wl">
                    <CheckCircleIcon className="h-3.5 w-3.5 text-green-500" data-id="element-3173" data-mp="211zc5f3k7uw0" />
                    Agile Delivery: bi-weekly sprints
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-700" data-id="element-3174" data-mp="1qxj252crh804">
                    <CheckCircleIcon className="h-3.5 w-3.5 text-green-500" data-id="element-3175" data-mp="1vvkmrrpu5fdp" />
                    Upgrade-Safe Code: out-of-box prioritized
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Platform Ops */}
            <motion.div custom={1} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="bg-white rounded-xl overflow-hidden border border-gray-200" data-id="element-3176" data-mp="187t84hgtoq99">
              <div className="bg-nrs-blue px-6 py-5" data-id="element-3177" data-mp="1i579ray37ahi">
                <div className="text-blue-200 font-heading font-bold text-xs uppercase tracking-wider mb-2" data-id="element-3178" data-mp="1i3mgdonidxl8">
                  Managed Services
                </div>
                <h3 className="font-heading font-bold text-xl text-white" data-id="element-3179" data-mp="pg0b8bymukw9">
                  Platform Operations & Maintenance
                </h3>
              </div>
              <div className="px-6 py-5" data-id="element-3180" data-mp="28ltfhqdi5s73">
                <p className="text-gray-600 text-sm mb-4" data-id="element-3181" data-mp="28mmpd07rcxtg">
                  Day-to-day administration and ongoing enhancements with
                  governance included.
                </p>
                <ul className="space-y-2 mb-4" data-id="element-3182" data-mp="2im3tveb9x5nu">
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-3183" data-mp="2dlpjvqq43ln6">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-3184" data-mp="2dnshowxiaj2g" />
                    User provisioning and incident support (P1-P4 with SLAs)
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-3185" data-mp="2nhswjqilb2od">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-3186" data-mp="31a8r02xfdsdo" />
                    Monthly feature releases
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-3187" data-mp="3b8eugoyvr6kn">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-3188" data-mp="36adam1tmr384" />
                    Governance included (architecture reviews, data governance,
                    ATO compliance)
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-3189" data-mp="3tw75x2826vmz">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-3190" data-mp="3fyiknta2xmhe" />
                    Platform monitoring and optimization
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-3191" data-mp="367xlo4qhufxy">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-3192" data-mp="36632auuvevvz" />
                    License management
                  </li>
                </ul>
                <div className="bg-nrs-light rounded-lg p-3 border border-nrs-blue/20" data-id="element-3193" data-mp="2x8ss7ctshcsn">
                  <div className="font-heading font-semibold text-xs text-nrs-blue mb-1" data-id="element-3194" data-mp="327421nk33nav">
                    SLA Commitments
                  </div>
                  <div className="text-xs text-gray-600" data-id="element-3195" data-mp="2s84yuki38jv9">
                    P1: 2-hour response / 8-hour resolution
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Specialized */}
            <motion.div custom={2} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="bg-white rounded-xl overflow-hidden border border-gray-200 lg:col-span-2" data-id="element-3196" data-mp="2wcvpigdpkr1p">
              <div className="bg-gray-800 px-6 py-5" data-id="element-3197" data-mp="2mdwmpefmjpmm">
                <div className="text-gray-300 font-heading font-bold text-xs uppercase tracking-wider mb-2" data-id="element-3198" data-mp="3a2z1meivj4df">
                  Specialized Capabilities
                </div>
                <h3 className="font-heading font-bold text-xl text-white" data-id="element-3199" data-mp="30dfid7r918i1">
                  Specialized Development Services
                </h3>
              </div>
              <div className="px-6 py-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-3200" data-mp="35apibhc9l2k4">
                <div className="border border-gray-100 rounded-lg p-4" data-id="element-3201" data-mp="3t0s5pk58hkce">
                  <div className="font-heading font-bold text-sm text-nrs-navy mb-1" data-id="element-3202" data-mp="1ogzetfm6j1q">
                    Secure On-Premise Instances
                  </div>
                  <p className="text-xs text-gray-600" data-id="element-3203" data-mp="1herbm9mmgfxc">
                    Bi-directional data sync, multi-instance architectures.
                    Cleared developers.
                  </p>
                </div>
                <div className="border border-gray-100 rounded-lg p-4" data-id="element-3204" data-mp="1r4159tx466nk">
                  <div className="font-heading font-bold text-sm text-nrs-navy mb-1" data-id="element-3205" data-mp="1r6x55nsiv5en">
                    Custom Module Development
                  </div>
                  <p className="text-xs text-gray-600" data-id="element-3206" data-mp="yh6z8211n9tm">
                    Mission-specific applications built on the ServiceNow
                    platform.
                  </p>
                </div>
                <div className="border border-gray-100 rounded-lg p-4" data-id="element-3207" data-mp="18da1d7vwb8gk">
                  <div className="font-heading font-bold text-sm text-nrs-navy mb-1" data-id="element-3208" data-mp="2nuad62k78ooi">
                    Integration Engineering
                  </div>
                  <p className="text-xs text-gray-600" data-id="element-3209" data-mp="2jvb17y01vrnk">
                    Complex API integrations, REST/SOAP, MID Server
                    configurations.
                  </p>
                </div>
                <div className="border border-gray-100 rounded-lg p-4" data-id="element-3210" data-mp="1lji7pdv88vtr">
                  <div className="font-heading font-bold text-sm text-nrs-navy mb-1" data-id="element-3211" data-mp="1qh0asnmkcgtu">
                    Platform Rescue & Remediation
                  </div>
                  <p className="text-xs text-gray-600" data-id="element-3212" data-mp="xscvd8ohwlo7">
                    Technical debt reduction, upgrade unblocking, Instance Scan
                    remediation.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* COMBINED PACKAGES */}
      <section className="section-white" data-id="element-3213" data-mp="1bk15j6f6p02j">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-3214" data-mp="1bl2ft8odlij7">
          <div className="text-center mb-10" data-id="element-3215" data-mp="f139dzxjg4fq">
            <div className="inline-block px-3 py-1 bg-nrs-orange/10 border border-nrs-orange/30 rounded-full text-nrs-orange text-xs font-semibold uppercase tracking-wider mb-3" data-id="element-3216" data-mp="j2f1qd7sr3rm">
              Most Popular
            </div>
            <h2 className="text-3xl font-bold text-center mb-4" data-id="element-3217" data-mp="6le2dvi7ht10">
              Combined Packages
            </h2>
            <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-6" data-id="element-3218" data-mp="2h69rn8mnsxi"></div>
            <p className="text-gray-600 mt-2" data-id="element-3219" data-mp="3tnzc4uf5py3m">
              Governance + Development + Operations — everything under one roof
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-3220" data-mp="3jz7now9b1vxg">
            <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="border-2 border-nrs-orange rounded-xl overflow-hidden" data-id="element-3221" data-mp="3jxwl2zrliu7x">
              <div className="bg-nrs-orange px-6 py-5" data-id="element-3222" data-mp="3awg94nl4uk1l">
                <div className="flex items-center gap-2 mb-2" data-id="element-3223" data-mp="35yepa0fvugp2">
                  <StarIcon className="h-5 w-5 text-white" data-id="element-3224" data-mp="310d51c6q0bc0" />
                  <span className="text-white font-heading font-bold text-xs uppercase tracking-wider" data-id="element-3225" data-mp="310v9gw3jdvv0">
                    Complete Solution
                  </span>
                </div>
                <h3 className="font-heading font-bold text-xl text-white" data-id="element-3226" data-mp="19v0al4z447d1">
                  Complete Platform Stewardship
                </h3>
              </div>
              <div className="px-6 py-5" data-id="element-3227" data-mp="105yt1acx5hwy">
                <p className="text-gray-600 text-sm mb-4" data-id="element-3228" data-mp="104xir83q8zga">
                  Governance + Development + Operations. Everything under one
                  roof.
                </p>
                <ul className="space-y-2 mb-4" data-id="element-3229" data-mp="q9dkwg5lrqow">
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-3230" data-mp="q5zmtk3z23n9">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-3231" data-mp="cae1po0v0404" />
                    Fractional Platform Owner (governance)
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-3232" data-mp="26nvp9b19l0v5">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-3233" data-mp="2blx57n3e8k2e" />
                    Platform Operations & Maintenance
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-3234" data-mp="3r4v514x87l69">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-3235" data-mp="3r2s43pujc7mr" />
                    Development capacity (monthly sprints)
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-3236" data-mp="4vtteofluw2r">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-3237" data-mp="ini7gx9ezumd" />
                    Application rationalization
                  </li>
                </ul>
                <div className="bg-orange-50 rounded-lg p-3 border border-orange-100" data-id="element-3238" data-mp="sn93x4hjcjp3">
                  <p className="text-xs text-orange-800 font-medium" data-id="element-3239" data-mp="nnd0b6cr2ua2">
                    Best For: Small-to-medium agencies (500-5,000 users) needing
                    complete ServiceNow partnership
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div custom={1} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="border-2 border-nrs-blue rounded-xl overflow-hidden" data-id="element-3240" data-mp="3f3ofevijjhlo">
              <div className="bg-nrs-blue px-6 py-5" data-id="element-3241" data-mp="3o3ju3abb1uqg">
                <div className="flex items-center gap-2 mb-2" data-id="element-3242" data-mp="3o662fc60se9q">
                  <StarIcon className="h-5 w-5 text-white" data-id="element-3243" data-mp="3t47irpc29zhi" />
                  <span className="text-white font-heading font-bold text-xs uppercase tracking-wider" data-id="element-3244" data-mp="1wv1lab6hm1l">
                    Modernization Program
                  </span>
                </div>
                <h3 className="font-heading font-bold text-xl text-white" data-id="element-3245" data-mp="ap6r8tygx5gi">
                  Platform Modernization Program
                </h3>
              </div>
              <div className="px-6 py-5" data-id="element-3246" data-mp="fnrtt0170184">
                <p className="text-gray-600 text-sm mb-4" data-id="element-3247" data-mp="kncp90h5lrg3">
                  Application Rationalization + Migration Development +
                  Governance.
                </p>
                <ul className="space-y-2 mb-4" data-id="element-3248" data-mp="pk379tciqx45">
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-3249" data-mp="2a5jeqyytjl0i">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-3250" data-mp="254vr8n9stmb1" />
                    60-day application rationalization
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-3251" data-mp="20858ttaiuemg">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-3252" data-mp="1v927ojxnwsjr" />
                    Phased migration development (6-24 months)
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700" data-id="element-3253" data-mp="2nx7st7smojk4">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-3254" data-mp="2j44tej166fw0" />
                    Governance framework implementation
                  </li>
                </ul>
                <div className="bg-nrs-light rounded-lg p-3 border border-nrs-blue/20" data-id="element-3255" data-mp="3blqx7c8k4c6x">
                  <p className="text-xs text-nrs-blue font-medium" data-id="element-3256" data-mp="36u8vun2mg9lf">
                    Best For: Agencies with 50+ legacy applications
                    consolidating onto ServiceNow
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-navy" data-id="element-3257" data-mp="2682m3vcm0q0e">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15 text-center" data-id="element-3258" data-mp="2b5lzkt1hgyra">
          <h2 className="text-3xl font-bold text-white mb-4" data-id="element-3259" data-mp="2b3hu46nxrrow">
            Not Sure Which Service Fits?
          </h2>
          <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-8" data-id="element-3260" data-mp="32ws73ujj9yql"></div>
          <p className="text-xl text-nrs-light mb-8 max-w-2xl mx-auto" data-id="element-3261" data-mp="32ufp5hdrzodj">
            Schedule a free 30-minute platform assessment. We'll recommend the
            right engagement model for your situation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-3262" data-mp="1di3srnrdu6px">
            <Link to="/services/servicenow/calculator" className="btn-primary" data-id="element-3263" data-mp="1djwxuymckivt">
              Calculate Your Risk Score First
            </Link>
            <a href="mailto:info@newriversystems.com" className="btn-secondary" data-id="element-3264" data-mp="1awmk9e1i14h7">
              Schedule Free Assessment
            </a>
          </div>
        </div>
      </section>
    </div>;
}